/**
 * Resolve ?rz=form&propertyId=… into an owned analysis for the authenticated user.
 * Used by Immo 360 → Rentalyzer conversion opening — never selects broker/shared foreign targets
 * and never falls back to a previous/blank currentProperty.
 */

export const OWNED_ANALYSIS_DEEP_LINK_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  NOT_FOUND: 'not_found',
  PERMISSION_DENIED: 'permission_denied',
  READ_FAILED: 'read_failed',
  CANCELLED: 'cancelled',
});

/**
 * @param {string | null | undefined} value
 */
function trimId(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * Classify a property already present in the combined portfolio snapshot.
 * Owned = exact id + uid matches authenticated user.
 * Foreign/shared entries in the combined list are permission failures, not selectable.
 *
 * @param {{
 *   propertyId?: string | null,
 *   authUid?: string | null,
 *   properties?: Array<{ id?: string, uid?: string }> | null,
 *   propertiesLoading?: boolean,
 * }} params
 * @returns {{
 *   status: string,
 *   property?: object | null,
 *   needsDirectRead?: boolean,
 * }}
 */
export function classifyOwnedAnalysisPortfolioMatch({
  propertyId = null,
  authUid = null,
  properties = null,
  propertiesLoading = false,
} = {}) {
  const targetId = trimId(propertyId);
  if (!targetId) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.IDLE, property: null };
  }
  const uid = trimId(authUid);
  if (!uid) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.LOADING, property: null };
  }
  if (propertiesLoading) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.LOADING, property: null };
  }

  const list = Array.isArray(properties) ? properties : [];
  const ownedMatch = list.find(
    (item) => trimId(item?.id) === targetId && trimId(item?.uid) === uid,
  );
  if (ownedMatch) {
    return {
      status: OWNED_ANALYSIS_DEEP_LINK_STATUS.READY,
      property: ownedMatch,
    };
  }

  const foreignMatch = list.find(
    (item) => trimId(item?.id) === targetId && trimId(item?.uid) !== uid,
  );
  if (foreignMatch) {
    return {
      status: OWNED_ANALYSIS_DEEP_LINK_STATUS.PERMISSION_DENIED,
      property: null,
    };
  }

  // Absence from the current cached list alone is not proof of deletion —
  // a newly created analysis may still be propagating into the portfolio subscription.
  return {
    status: OWNED_ANALYSIS_DEEP_LINK_STATUS.LOADING,
    property: null,
    needsDirectRead: true,
  };
}

/**
 * Classify a one-shot property document read for owned deep-link resolution.
 *
 * @param {{
 *   propertyId?: string | null,
 *   authUid?: string | null,
 *   property?: { id?: string, uid?: string } | null,
 *   readError?: { code?: string, message?: string } | Error | null,
 * }} params
 */
export function classifyOwnedAnalysisPropertyRead({
  propertyId = null,
  authUid = null,
  property = null,
  readError = null,
} = {}) {
  const targetId = trimId(propertyId);
  const uid = trimId(authUid);
  if (!targetId || !uid) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED, property: null };
  }

  if (readError) {
    const code = String(readError.code || readError.message || '').toLowerCase();
    if (
      code.includes('permission-denied') ||
      code.includes('permission_denied') ||
      code.includes('unauthorized')
    ) {
      return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.PERMISSION_DENIED, property: null };
    }
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.READ_FAILED, property: null };
  }

  if (!property || trimId(property.id) !== targetId) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.NOT_FOUND, property: null };
  }

  if (trimId(property.uid) !== uid) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.PERMISSION_DENIED, property: null };
  }

  return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.READY, property };
}

/**
 * Full resolution step used by RentalPropertyAnalyzer (and behavioral tests).
 * Protects against stale async responses via isCurrent().
 *
 * @param {{
 *   propertyId?: string | null,
 *   authUid?: string | null,
 *   properties?: Array<object> | null,
 *   propertiesLoading?: boolean,
 *   readProperty?: (propertyId: string) => Promise<object | null>,
 *   isCurrent?: () => boolean,
 * }} params
 */
export async function resolveOwnedAnalysisPropertyDeepLink({
  propertyId = null,
  authUid = null,
  properties = null,
  propertiesLoading = false,
  readProperty = null,
  isCurrent = () => true,
} = {}) {
  const portfolio = classifyOwnedAnalysisPortfolioMatch({
    propertyId,
    authUid,
    properties,
    propertiesLoading,
  });

  if (
    portfolio.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.IDLE ||
    portfolio.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.READY ||
    portfolio.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.PERMISSION_DENIED
  ) {
    if (!isCurrent()) {
      return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED, property: null };
    }
    return portfolio;
  }

  if (!portfolio.needsDirectRead) {
    if (!isCurrent()) {
      return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED, property: null };
    }
    return portfolio;
  }

  if (typeof readProperty !== 'function') {
    // Without a reader, treat absence after load as not found (caller should inject getPropertyOnce).
    if (!isCurrent()) {
      return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED, property: null };
    }
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.NOT_FOUND, property: null };
  }

  const targetId = trimId(propertyId);
  let property = null;
  let readError = null;
  try {
    property = await readProperty(targetId);
  } catch (err) {
    readError = err;
  }

  if (!isCurrent()) {
    return { status: OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED, property: null };
  }

  return classifyOwnedAnalysisPropertyRead({
    propertyId: targetId,
    authUid,
    property,
    readError,
  });
}

/**
 * Whether the analyzer may render an editable property form for this resolution.
 * Loading / failure must not expose a blank create form or a previous analysis.
 */
export function shouldRenderOwnedAnalysisEditor(resolution) {
  return resolution?.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.READY && resolution.property != null;
}

/**
 * Whether propertyId should remain in the URL.
 * Keep through loading and failure until the user dismisses; clear only after apply or dismiss.
 */
export function shouldKeepOwnedAnalysisPropertyIdInUrl(resolution) {
  const status = resolution?.status;
  if (
    !status ||
    status === OWNED_ANALYSIS_DEEP_LINK_STATUS.IDLE ||
    status === OWNED_ANALYSIS_DEEP_LINK_STATUS.CANCELLED
  ) {
    return false;
  }
  return true;
}

/**
 * Clear propertyId from search only after a definitive apply or user-dismissed failure.
 */
export function shouldClearOwnedAnalysisPropertyIdFromUrl(resolution, { applied = false, dismissed = false } = {}) {
  if (applied && resolution?.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.READY) return true;
  if (
    dismissed &&
    (resolution?.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.NOT_FOUND ||
      resolution?.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.PERMISSION_DENIED ||
      resolution?.status === OWNED_ANALYSIS_DEEP_LINK_STATUS.READ_FAILED)
  ) {
    return true;
  }
  return false;
}
